const DEFAULT_API_BASE_URL = '';

export const AUTH_TOKEN_COOKIE = 'yakson_access_token';
export const AUTH_USER_TYPE_COOKIE = 'yakson_user_type';

let accessToken: string | null = null;

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function getApiBaseUrl(): string {
  const baseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    DEFAULT_API_BASE_URL;

  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

export function buildApiUrl(path: string): string {
  return `${getApiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCookie(name: string): string | undefined {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  return match?.split('=').slice(1).join('=');
}

export function setCookie(name: string, value: string, maxAge = 60 * 60 * 24): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
}

export function deleteCookie(name: string): void {
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function clearAuthCookies(): void {
  deleteCookie(AUTH_TOKEN_COOKIE);
  deleteCookie(AUTH_USER_TYPE_COOKIE);
}

/**
 * Decode a JWT payload (no signature verification — the FastAPI backend verifies
 * on every request; this only reads the principal's claims for the client UI).
 * The FastAPI `/api/auth/me` returns only `{ outletId }` (byte-identical to the
 * legacy route), so identity — id (`sub`) / email / userType / staffId / outletId
 * — is carried in the access token, exactly as the backend auth contract intends.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );

    let json: string;
    if (typeof atob === 'function') {
      json = decodeURIComponent(
        atob(padded)
          .split('')
          .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join(''),
      );
    } else {
      json = Buffer.from(padded, 'base64').toString('utf-8');
    }

    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** True only when the token has an `exp` claim that is in the past. */
export function isTokenExpired(token: string): boolean {
  const claims = decodeJwtPayload(token);
  const exp = claims?.exp;
  if (typeof exp !== 'number') return false;
  return Date.now() >= exp * 1000;
}

function handleUnauthorized(): void {
  accessToken = null;

  clearAuthCookies();
  window.dispatchEvent(new Event('yakson-auth:logout'));
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  const token = accessToken ?? getCookie(AUTH_TOKEN_COOKIE) ?? null;

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers,
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError(401, 'Authentication required');
  }

  if (!response.ok) {
    const body = await response.text();
    let message = body || `Request failed with status ${response.status}`;

    try {
      const parsed = JSON.parse(body) as { detail?: string; message?: string };
      message = parsed.detail || parsed.message || message;
    } catch {
      // Ignore parse failures and fall back to raw body text.
    }

    throw new ApiError(response.status, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
